import { create } from 'zustand';
import { ApiRequestError } from '@/lib/apiClient';
import { parentApi } from '../api/parent.api';
import type { ParentChild } from '../types/parent';

interface ParentChildState {
  children: ParentChild[];
  /** The child every screen in the panel is currently about. */
  selectedUuid: string;
  /** True until the first load settles. Every screen waits on it. */
  isLoading: boolean;
  error: string | null;
  load: () => Promise<void>;
  select: (uuid: string) => void;
}

/**
 * Which child the parent panel is looking at.
 *
 * A store rather than page state because two places need the same answer at
 * once: the switcher in the sidebar sets it, and every screen inside the frame
 * reads it. Held in page state instead, moving from Homework to Attendance
 * would quietly put the other child's records on screen — which, in a product
 * holding children's data, is the one mistake that must not be possible.
 *
 * The list comes from `GET /parent/children`, which answers from the caller's
 * own guardian links. Loaded once by `ParentLayout`, the way `branding.store`
 * loads the institute's own name and logo.
 */
export const useParentChild = create<ParentChildState>((set, get) => ({
  children: [],
  selectedUuid: '',
  isLoading: true,
  error: null,

  load: async () => {
    set({ isLoading: true, error: null });

    try {
      const children = await parentApi.children();

      // Keep the parent on the child they were reading, when that child is
      // still on the roll: a reload must not silently move them to a sibling.
      const previous = get().selectedUuid;
      const selectedUuid = children.some((child) => child.uuid === previous)
        ? previous
        : (children[0]?.uuid ?? '');

      set({ children, selectedUuid, isLoading: false });
    } catch (caught) {
      set({
        isLoading: false,
        error:
          caught instanceof ApiRequestError
            ? caught.message
            : 'Something went wrong. Please try again.',
      });
    }
  },

  select: (uuid) => set({ selectedUuid: uuid }),
}));

/**
 * The child on screen, or null before the list has arrived.
 *
 * Falls back to the first child rather than to nothing when the selected id is
 * not in the list — a family whose second child has left the school should not
 * find the panel empty.
 */
export function useSelectedChild(): ParentChild | null {
  const children = useParentChild((state) => state.children);
  const selectedUuid = useParentChild((state) => state.selectedUuid);

  return children.find((child) => child.uuid === selectedUuid) ?? children[0] ?? null;
}
